import React, {useMemo} from 'react';
import {Grid} from '@mui/material';
import type {LinkedLiterary} from 'schemaDefinition/types';
import type {UpdateLog, WorkV4} from 'services/metadata/types';
import {formatDate, useLocalization} from 'localization';
import {isLinkedLiterary} from 'schemaDefinition';
import {useCodelist} from 'services/codelists';
import {useAssertSavedManifestation} from 'services/metadata/hooks/useAssertSavedManifestation';
import {getFirstName} from 'services/users';
import {useGetUsersQuery} from 'services/users/usersApi';
import {
  FlexBox,
  HtmlReadonly,
  Icon,
  ProductStatusIcon,
  useProductStatusTips,
} from 'components';
import {formatLinkedValueLink} from 'schema/preview/functions';
import {findFirstOfEachStatus} from '../functions/findFirstOfEachStatus';
import {ManifestationSubPanelCard} from './ManifestationCard/components/ManifestationSubPanelCard';
import {
  ManifestationSubPanelItem,
  type ManifestationSubPanelItemProps,
} from './ManifestationCard/components/ManifestationSubPanelItem';
import {ManifestationPanelContainer} from './ManifestationPanelContainer';
import {ManifestationStatus} from './ManifestationStatus';

type Props = {
  manifestationId: string;
  workSuggestions?: Partial<WorkV4>;
  onClose: () => void;
};

export const InfoPanel: React.FC<Props> = ({
  manifestationId,
  workSuggestions,
  onClose,
}) => {
  const {t} = useLocalization();
  const manifestation = useAssertSavedManifestation(manifestationId);
  const productStatusTips = useProductStatusTips();
  const publisherStatuses = useCodelist('PUBLISHER_STATUS');
  const distributorStatuses = useCodelist('DISTRIBUTOR_STATUS');
  const {data: users} = useGetUsersQuery();

  const {
    metadata,
    suggestions,
    ddsData,
    marketingData,
    marketingDescriptions,
    statusUpdateLogs,
  } = useMemo(() => {
    const metadata: ManifestationSubPanelItemProps[] = [];
    if (manifestation.registered) {
      metadata.push({
        fieldName: 'registered',
        value: formatDate(manifestation.registered),
      });
    }

    if (manifestation.completedDate) {
      metadata.push({
        fieldName: 'completedDate',
        value: formatDate(manifestation.completedDate),
      });
    }

    const suggestions: ManifestationSubPanelItemProps[] = [];
    const workSuggestionKeys = Object.keys(workSuggestions ?? {}) as Array<
      keyof WorkV4
    >;
    if (workSuggestionKeys.length) {
      workSuggestionKeys.forEach(key => {
        const value = workSuggestions?.[key];
        if (value) {
          if (Array.isArray(value) && isLinkedLiterary(value[0])) {
            suggestions.push({
              fieldName: key,
              value: (value as LinkedLiterary[]).map(v => (
                <FlexBox horizontal>
                  {formatLinkedValueLink(v.link, v.numberInSeries)}
                </FlexBox>
              )),
            });
          } else {
            suggestions.push({
              fieldName: key,
              value: Array.isArray(value) ? value.join(', ') : value,
            });
          }
        }
      });
    }

    const ddsData: ManifestationSubPanelItemProps[] = [];
    if (manifestation.ddsStatus?.length) {
      if (manifestation.ddsStatus.length === 1) {
        ddsData.push({
          fieldName: 'ddsStatus',
          value: t(
            `entity.manifestation.ddsStatus.${manifestation.ddsStatus[0].ddsPublishStatus}`,
          ),
        });
      } else {
        manifestation.ddsStatus.forEach(status => {
          ddsData.push({
            fieldName: 'ddsStatus',
            value: `${t(
              `entity.manifestation.ddsStatus.${status.ddsPublishStatus}`,
            )} (${status.productOwnerCode})`,
          });
        });
      }
    }

    const marketingData: ManifestationSubPanelItemProps[] = [];
    const marketingDescriptions: ManifestationSubPanelItemProps[] = [];
    if (manifestation.products?.length) {
      manifestation.products.forEach(product => {
        marketingData.push({
          fieldName: 'productOwner',
          value: product.owner,
        });

        if (product.inSaleDate) {
          marketingData.push({
            fieldName: 'inSaleDate',
            value: formatDate(product.inSaleDate),
          });
        }

        const publisherStatus = product.publisherStatus
          ? publisherStatuses.codes.find(
              c => c.code === product.publisherStatus,
            )
          : undefined;
        if (publisherStatus) {
          marketingData.push({
            fieldName: 'publisherStatus',
            value: `${publisherStatus.code}: ${publisherStatus.value}`,
          });
        }

        if (product.publisherSalesDate) {
          marketingData.push({
            fieldName: 'publisherSalesDate',
            value: formatDate(product.publisherSalesDate),
          });
        }

        const distributorStatus = product.distributorStatus
          ? distributorStatuses.codes.find(
              c => c.code === product.distributorStatus,
            )
          : undefined;
        if (distributorStatus) {
          marketingData.push({
            fieldName: 'distributorStatus',
            value: `${distributorStatus.code}: ${distributorStatus.value}`,
          });
        }

        if (product.comingDate) {
          marketingData.push({
            fieldName: 'comingDate',
            value: formatDate(product.comingDate),
          });
        }

        if (product.status) {
          marketingData.push({
            fieldName: 'products.status',
            value: (
              <FlexBox horizontal left gap={0.5}>
                <ProductStatusIcon productStatus={product.status} />
                {productStatusTips[product.status]}
              </FlexBox>
            ),
          });
        }

        if (product.distributor) {
          marketingData.push({
            fieldName: 'products.distributor',
            value: product.distributor,
          });
        }

        if (product.hidden !== undefined) {
          const status = product.hidden
            ? t('form.global.type.bool.preview.true')
            : t('form.global.type.bool.preview.false');

          marketingData.push({
            fieldName: 'hidden',
            value: (
              <FlexBox horizontal left gap={0.5}>
                {product.hidden ? <Icon icon="Hidden" /> : null}
                {status}
              </FlexBox>
            ),
          });
        }

        if (product.measures?.length) {
          product.measures.forEach(m => {
            marketingData.push({
              fieldName: `products.measure.${m.type}`,
              value: `${m.value} ${
                m.unit
                  ? t(`form.global.field.products.measure.${m.unit}.label`)
                  : null
              }`,
            });
          });
        }

        if (product.descriptions) {
          product.descriptions.forEach(d => {
            marketingDescriptions.push({
              fieldName: `products.description.${
                ['nob', 'nor', 'eng'].includes(d.language) ? d.language : 'und'
              }`,
              value: <HtmlReadonly value={d.content} />,
            });
          });
        }
      });
    }

    const statusUpdateLogs: ManifestationSubPanelItemProps[] = [];
    const logs = findFirstOfEachStatus(manifestation.updateLogs ?? []);
    if (Object.keys(logs).length) {
      const formatAudit = (log: UpdateLog) => {
        const user = users?.find(u => u.id === log.updatedBy);
        return `${log.created ? formatDate(log.created * 1000) : null} av ${getFirstName(user)}`;
      };

      statusUpdateLogs.push({
        fieldName: `statusUpdate`,
        value: (
          <>
            {logs.COMPLETED ? (
              <Grid
                container
                width={'100%'}
                spacing={1}
                alignItems="center"
                mb={0.5}>
                <Grid size={5}>
                  <ManifestationStatus status={'COMPLETED'} mode={'mini'} />
                </Grid>
                <Grid size={7}>{formatAudit(logs.COMPLETED)}</Grid>
              </Grid>
            ) : null}
            {logs.CONTROL ? (
              <Grid
                container
                width={'100%'}
                spacing={1}
                alignItems="center"
                mb={0.5}>
                <Grid size={5}>
                  <ManifestationStatus status={'CONTROL'} mode={'mini'} />
                </Grid>
                <Grid size={7}>{formatAudit(logs.CONTROL)}</Grid>
              </Grid>
            ) : null}
            {logs.PRE_CATALOGED ? (
              <Grid
                container
                width={'100%'}
                spacing={1}
                alignItems="center"
                mb={0.5}>
                <Grid size={5}>
                  <ManifestationStatus status={'PRE_CATALOGED'} mode={'mini'} />
                </Grid>
                <Grid size={7}>{formatAudit(logs.PRE_CATALOGED)}</Grid>
              </Grid>
            ) : null}
          </>
        ),
      });
    }

    return {
      metadata,
      suggestions,
      ddsData,
      marketingData,
      marketingDescriptions,
      statusUpdateLogs,
    };
  }, [
    manifestation.registered,
    manifestation.completedDate,
    manifestation.ddsStatus,
    manifestation.products,
    manifestation.updateLogs,
    workSuggestions,
    t,
    publisherStatuses.codes,
    distributorStatuses.codes,
    productStatusTips,
    users,
  ]);

  return (
    <ManifestationPanelContainer
      title={t('page.metadata.manifestation.tools.info')}
      icon={'InfoCircle'}
      onClose={onClose}>
      <ManifestationSubPanelCard>
        {metadata.map(d => (
          <ManifestationSubPanelItem key={d.fieldName} {...d} />
        ))}
      </ManifestationSubPanelCard>
      <ManifestationSubPanelCard>
        {ddsData.map(d => (
          <ManifestationSubPanelItem key={d.fieldName} {...d} />
        ))}
      </ManifestationSubPanelCard>
      <ManifestationSubPanelCard>
        {ddsData.map(d => (
          <ManifestationSubPanelItem key={d.fieldName} {...d} />
        ))}
      </ManifestationSubPanelCard>

      <ManifestationSubPanelCard>
        {marketingData.map(m => (
          <ManifestationSubPanelItem key={m.fieldName} {...m} />
        ))}
      </ManifestationSubPanelCard>

      <ManifestationSubPanelCard>
        {statusUpdateLogs.map(m => (
          <ManifestationSubPanelItem key={m.fieldName} {...m} />
        ))}
      </ManifestationSubPanelCard>

      <ManifestationSubPanelCard>
        {marketingDescriptions.map(m => (
          <ManifestationSubPanelItem key={m.fieldName} {...m} />
        ))}
      </ManifestationSubPanelCard>

      <ManifestationSubPanelCard>
        {suggestions.map(m => (
          <ManifestationSubPanelItem key={m.fieldName} {...m} />
        ))}
      </ManifestationSubPanelCard>
    </ManifestationPanelContainer>
  );
};
